import {
  Button,
  Icon,
  IconType,
  InputContainer,
  ProfilePicture,
  TabbedDrawer,
  useTabbedTabContext,
  InputIcon,
  InputText,
  TabbedDrawerHeader,
  TabbedDrawerTitle,
  TabbedDrawerTab,
  TabbedDrawerCTA,
} from "@sellar-io/components";
import { InfiniteData, UseInfiniteQueryResult } from "@tanstack/react-query";
import { ReactNode, useState } from "react";
import { twMerge } from "tailwind-merge";

import {
  StorefrontPreviewProductListResponseType,
  StorefrontPreviewCustomerListResponseType,
  StorefrontPreviewCustomerZoneListResponseType,
  StorefrontPreviewOrderRulesListResponseType,
} from "@sellar-io/api-v2/types";
import { formatCurrency } from "@sellar-io/common";
import { generatePriceTierOrderRuleDescription } from "../../utils/sellarHelpers";
import { InfiniteList } from "../infiniteLoading/InfiniteList";
import { SkeletonContainer, SkeletonText } from "../skeleton/Skeleton";
import { Disclosure, DisclosureButton } from "@headlessui/react";
import {
  StorefrontPreviewerTabs,
  tabOptions,
  isThirdParty,
} from "./StorefrontPreviewer.shared";
import { SlideTransition } from "../transition/SlideTransition";

type CustomerZone = Omit<
  StorefrontPreviewCustomerZoneListResponseType[number],
  "SupplierId" | "VolumeDiscountsId"
>;

type BaseSelection = {
  availabilityTierId: number | null;
  priceTierId: number | null;
  orderRulesId: number | null;
};

export type SelectedCustomer = BaseSelection & {
  connectionId: number | null;
};

export type SelectedCustomerZone = BaseSelection & {
  customerZoneId: number | null;
};

type Customer = StorefrontPreviewCustomerListResponseType[number];

type StorefrontPreviewerProps = {
  trigger: ReactNode;
  open: boolean;
  initialTab: StorefrontPreviewerTabs;
  businessName: string;
  customerZoneSearchProps: {
    customerZones: CustomerZone[];
    isSearchingForCustomerZone: boolean;
    selectedCustomerZone: SelectedCustomerZone | null;
    onSearchForCustomerZone: (searchTerm: string) => void;
    onSelectCustomerZone: (params: SelectedCustomerZone | null) => void;
  };
  customerSearchProps: {
    customers: Customer[];
    isSearchingForCustomer: boolean;
    selectedCustomer: SelectedCustomer | null;
    onSearchForCustomer: (searchTerm: string) => void;
    onSelectCustomer: (params: SelectedCustomer | null) => void;
  };
  productListQuery: UseInfiniteQueryResult<
    InfiniteData<StorefrontPreviewProductListResponseType, unknown>
  >;
  orderRule: {
    name: string;
    minimumOrderQuantities: Array<
      StorefrontPreviewOrderRulesListResponseType["PriceTierOrderRules"][number]
    >;
    minimumOrderValue: number;
  };

  featureFlags: {
    isOrderRulesEnabled: boolean;
  };
  onTogglePreviewer: (open: boolean) => void;
  onChangeTab: (tab: StorefrontPreviewerTabs) => void;
  onClickStorefrontPreview: () => void;
};

export const StorefrontPreviewer = (props: StorefrontPreviewerProps) => {
  return (
    <>
      {props.trigger}
      <TabbedDrawer
        active={props.open}
        setActive={props.onTogglePreviewer}
        initTab={props.initialTab}
      >
        <TabbedDrawerHeader className="bg-zinc-50">
          <TabbedDrawerTitle
            icon="STOREFRONT_OUTLINE"
            title="Preview"
            subtitle={props.businessName}
          />
        </TabbedDrawerHeader>
        <StorefrontPreviewerTabSelector {...props} />
        <TabbedDrawerTab tab={StorefrontPreviewerTabs.CUSTOMER}>
          <CustomerTab {...props} />
        </TabbedDrawerTab>
        <TabbedDrawerTab tab={StorefrontPreviewerTabs.CUSTOMER_ZONE}>
          <CustomerZoneTab {...props} />
        </TabbedDrawerTab>
        <TabbedDrawerCTA>
          <Button onClick={props.onClickStorefrontPreview} className="w-full">
            Storefront View
            <Icon icon="OPEN_IN_NEW" />
          </Button>
        </TabbedDrawerCTA>
      </TabbedDrawer>
    </>
  );
};

const StorefrontPreviewerTabSelector = ({
  onChangeTab,
}: Pick<StorefrontPreviewerProps, "onChangeTab">) => {
  const { tab, setTab } = useTabbedTabContext();

  return (
    <div className="flex bg-zinc-50 transition-all">
      {tabOptions.map((tabOption) => {
        const isActive = tab === tabOption.tab;
        return (
          <button
            type="button"
            key={tabOption.tab}
            className={twMerge(
              "flex-1 p-4 transition-colors",
              isActive
                ? "border-b-3 border-b-zinc-800 font-semibold text-black"
                : "cursor-pointer border-b-2 border-b-zinc-300 text-zinc-500",
            )}
            onClick={() => {
              setTab(tabOption.tab);
              onChangeTab(tabOption.tab);
            }}
          >
            {tabOption.label}
          </button>
        );
      })}
    </div>
  );
};

const CustomerZoneSelector = ({
  customerZoneSearchProps: {
    customerZones,
    isSearchingForCustomerZone,
    selectedCustomerZone: customerZoneSelection,
    onSelectCustomerZone,
    onSearchForCustomerZone,
  },
  featureFlags,
  loadingFallback,
}: StorefrontPreviewerProps & {
  loadingFallback: ReactNode;
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const selectedCustomerZone = customerZones.find((customerZone) => {
    return customerZone.id === customerZoneSelection?.customerZoneId;
  });
  const hasSelectedCustomerZone = !!customerZoneSelection;

  if (selectedCustomerZone && hasSelectedCustomerZone) {
    return (
      <div className="flex rounded-lg border border-zinc-300 p-4">
        <div className="flex flex-1 flex-col gap-1">
          <span className="font-semibold text-zinc-800">
            {selectedCustomerZone.name}
          </span>
          {isThirdParty(selectedCustomerZone) ? (
            <span className="text-sm">All Postcodes</span>
          ) : (
            <PostcodeRenderer postcodes={selectedCustomerZone.postcodes} />
          )}
          <div className="flex flex-wrap gap-2">
            <CustomerZoneBadge
              label="Price tier"
              icon="TAG_OUTLINE"
              value={
                selectedCustomerZone.PriceTier?.name ?? "No price tier set"
              }
            />
            <CustomerZoneBadge
              label="Product catalogue"
              icon="BOOK_OUTLINE"
              value={
                selectedCustomerZone.AvailabilityTier?.name ??
                "No product catalogue set"
              }
            />
            {featureFlags.isOrderRulesEnabled && (
              <CustomerZoneBadge
                label="Order rules"
                icon="CLIPBOARD_CHECK_OUTLINE"
                value={
                  selectedCustomerZone.OrderRules?.name ?? "No order rules set"
                }
              />
            )}
            {selectedCustomerZone.type === "direct" && (
              <CustomerZoneBadge
                value="Direct"
                className="bg-purple-800 text-white"
              />
            )}
            {selectedCustomerZone.type === "thirdParty" && (
              <CustomerZoneBadge
                value="Non-Direct"
                className="bg-purple-100 text-purple-800"
              />
            )}
          </div>
        </div>
        <div className="flex items-center justify-center">
          <button
            type="button"
            className="cursor-pointer"
            onClick={() => {
              onSelectCustomerZone(null);
            }}
          >
            <Icon icon="CLOSE" />
          </button>
        </div>
      </div>
    );
  }

  const renderList = () => {
    return (
      <div
        className={twMerge(
          "rounded-lg border-gray-200",
          customerZones.length > 0 ? "border" : "border-none",
        )}
      >
        {customerZones.map((customerZone) => {
          return (
            <button
              type="button"
              key={customerZone.id}
              onClick={() => {
                onSelectCustomerZone({
                  customerZoneId: customerZone.id,
                  availabilityTierId: customerZone.AvailabilityTierId,
                  priceTierId: customerZone.PriceTierId,
                  orderRulesId: customerZone.OrderRulesId,
                });
              }}
              className="flex w-full cursor-pointer flex-col p-2 text-start first-of-type:rounded-t-lg last-of-type:rounded-b-lg hover:bg-zinc-100"
            >
              <p className="font-semibold text-zinc-800">{customerZone.name}</p>
              {isThirdParty(customerZone) ? (
                <span className="text-sm">All Postcodes</span>
              ) : (
                <PostcodeRenderer postcodes={customerZone.postcodes} />
              )}
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-2">
      <InputContainer className="w-full">
        <InputIcon icon="MAGNIFY" />
        <InputText
          placeholder="Enter a name or postcode"
          value={searchTerm}
          onChange={(event) => {
            const value = event.target.value;
            setSearchTerm(value);
            onSearchForCustomerZone(value);
          }}
        />
      </InputContainer>
      {isSearchingForCustomerZone && loadingFallback}
      {!isSearchingForCustomerZone && customerZones.length > 0 && renderList()}
    </div>
  );
};

const CustomerZoneTab = (props: StorefrontPreviewerProps) => {
  return (
    <div className="flex flex-col gap-2 p-4">
      <CustomerZoneSelector {...props} loadingFallback={<LoadingFallback />} />
      {props.customerZoneSearchProps.selectedCustomerZone && (
        <>
          <ProductList {...props} />
          {props.featureFlags.isOrderRulesEnabled && (
            <OrderRulesInformation {...props} />
          )}
        </>
      )}
    </div>
  );
};

type OrderRulesInformationProps = StorefrontPreviewerProps;

const OrderRulesInformation = ({
  customerSearchProps: { selectedCustomer },
  customerZoneSearchProps: { selectedCustomerZone },
  orderRule,
}: OrderRulesInformationProps) => {
  const { tab } = useTabbedTabContext();

  const hasOrderRule = Boolean(orderRule);
  const hasSelectedACustomerZone =
    tab === StorefrontPreviewerTabs.CUSTOMER
      ? Boolean(selectedCustomer)
      : Boolean(selectedCustomerZone);

  const canToggleCollapsible = hasSelectedACustomerZone && hasOrderRule;

  const renderMinimumOrderQuantities = () => {
    if (orderRule.minimumOrderQuantities.length === 0) {
      return <span className="text-zinc-500">No order rules</span>;
    }
    return (
      <div className="flex flex-col">
        {orderRule.minimumOrderQuantities.map((moq) => (
          <div key={moq.id}>{generatePriceTierOrderRuleDescription(moq)}</div>
        ))}
      </div>
    );
  };

  const renderMinimumSpend = () => {
    if (!orderRule.minimumOrderValue) {
      return <span className="text-zinc-500">No minimum spend</span>;
    }

    return (
      <div>{formatCurrency(orderRule.minimumOrderValue)} minimum spend</div>
    );
  };

  return (
    <Disclosure as="div" defaultOpen={false}>
      {({ open }) => (
        <>
          <DisclosureButton
            type="button"
            disabled={!canToggleCollapsible}
            className="flex w-full cursor-pointer items-center justify-between rounded-lg border border-zinc-300 p-4 disabled:cursor-default"
          >
            <span className="font-semibold">Order Rules</span>
            {canToggleCollapsible && <ShowAndHideComponent open={open} />}
          </DisclosureButton>
          <SlideTransition show={open} parentClass="duration-200 ease-out">
            <div className="flex flex-col gap-4 py-4">
              <div className="divide-y divide-zinc-300 rounded-lg border border-zinc-300">
                <div className="flex flex-col gap-1 p-4 text-sm">
                  <p className="font-semibold">{orderRule.name}</p>
                  {renderMinimumSpend()}
                  {renderMinimumOrderQuantities()}
                </div>
              </div>
            </div>
          </SlideTransition>
        </>
      )}
    </Disclosure>
  );
};

const ShowAndHideComponent = ({ open }: { open: boolean }) => {
  const collapsibleIcon: IconType = open ? "CHEVRON_UP" : "CHEVRON_DOWN";

  const collapsibleText = open ? "Hide" : "Show";
  return (
    <span className="flex items-center gap-1">
      <span>{collapsibleText}</span>
      <span>
        <Icon icon={collapsibleIcon} className="min-h-6 min-w-6" />
      </span>
    </span>
  );
};

type ProductListProps = StorefrontPreviewerProps;

const ProductList = ({
  productListQuery,
  customerSearchProps,
  customerZoneSearchProps,
}: ProductListProps) => {
  const { tab } = useTabbedTabContext();
  const productList = productListQuery.data?.pages.flat() ?? [];

  const selectedCustomer =
    tab === StorefrontPreviewerTabs.CUSTOMER
      ? customerSearchProps.selectedCustomer
      : customerZoneSearchProps.selectedCustomerZone;

  const hasSelectedACustomerZone = Boolean(selectedCustomer);
  const nbProducts = productList.flatMap((parent) => parent.Products).length;
  const canToggleCollapsible = hasSelectedACustomerZone && nbProducts > 0;

  return (
    <Disclosure as="div" defaultOpen={false}>
      {({ open }) => (
        <>
          <DisclosureButton
            type="button"
            disabled={!canToggleCollapsible}
            className="flex w-full cursor-pointer items-center justify-between rounded-lg border border-zinc-300 p-4 disabled:cursor-default"
          >
            <span className="font-semibold">Products • {nbProducts}</span>
            {canToggleCollapsible && <ShowAndHideComponent open={open} />}
          </DisclosureButton>
          <SlideTransition show={open} parentClass="duration-200 ease-out">
            <InfiniteList
              query={productListQuery}
              className="flex flex-col gap-4 py-4"
            >
              {productList.map((productList) => {
                const parent = productList;

                const products = parent.Products.map((product) => {
                  const price = product.ProductPriceTiers[0]?.price;
                  const formattedPrice = price ? formatCurrency(price) : "N/A";
                  return {
                    id: product.id,
                    format: product.name,
                    price: formattedPrice,
                  };
                });

                return (
                  <div
                    key={parent.id}
                    className="rounded-lg border border-zinc-300"
                  >
                    <div className="flex items-center gap-2 p-4">
                      <ProfilePicture logoUrl={parent.imageUrl} size="medium" />
                      <div className="flex flex-col">
                        <span className="font-semibold">{parent.name}</span>
                        <span>
                          {parent.kind}
                          {" // "}
                          {parent.abv}%
                        </span>
                      </div>
                    </div>
                    <CustomGridTable<(typeof products)[number]>
                      columns={[
                        {
                          id: "format",
                          key: "format",
                          header: "Format",
                        },
                        {
                          id: "price",
                          key: "price",
                          header: "Price",
                        },
                      ]}
                      rows={products}
                      className="grid-cols-[minmax(200px,2fr)_minmax(100px,1fr)]"
                    />
                  </div>
                );
              })}
            </InfiniteList>
          </SlideTransition>
        </>
      )}
    </Disclosure>
  );
};

type CustomColumn<T> = {
  id: string;
  key: keyof T;
  header: ReactNode;
};

type CustomGridTableProps<T> = {
  columns: Array<CustomColumn<T>>;
  rows: T[];
  className: string;
};
const CustomGridTable = <T extends { id: number | string }>({
  columns,
  rows,
  className,
}: CustomGridTableProps<T>) => {
  const subgrid = "grid col-span-full grid-cols-subgrid";

  return (
    <div className={twMerge("grid overflow-x-auto", className)}>
      <div className={twMerge(subgrid, "border-y border-zinc-300")}>
        {columns.map((column) => (
          <div
            key={column.id}
            className="bg-zinc-50 px-4 py-3 text-sm font-semibold"
          >
            {column.header}
          </div>
        ))}
      </div>

      {rows.map((row) => (
        <div
          key={row.id}
          className={twMerge(
            subgrid,
            "border-b border-b-zinc-200 last-of-type:border-b-0",
          )}
        >
          {columns.map((column) => (
            <div key={column.id} className="px-4 py-3 font-semibold">
              {(row as any)[column.key]}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

const PostcodeRenderer = ({
  postcodes,
  nbVisiblePostcodes = 5,
}: {
  postcodes: string[];
  nbVisiblePostcodes?: number;
}) => {
  const nbHidden =
    postcodes.length > nbVisiblePostcodes
      ? postcodes.length - nbVisiblePostcodes
      : 0;

  const visiblePostcodes = postcodes.slice(0, 5);

  return (
    <div>
      <span>{visiblePostcodes.join(", ")} </span>
      {Boolean(nbHidden > 0) && (
        <span className="font-semibold">+{nbHidden} more</span>
      )}
    </div>
  );
};

const CustomerZoneBadge = ({
  label,
  value,
  className,
  icon,
}: {
  label?: string;
  value: string;
  className?: string;
  icon?: IconType;
}) => {
  return (
    <div
      className={twMerge(
        "flex items-center gap-1 rounded-lg border border-gray-300 bg-gray-100 px-2 py-1 text-sm",
        className,
      )}
    >
      {icon && <Icon icon={icon} />}
      {label && <span className="font-semibold">{label}:</span>}
      {value}
    </div>
  );
};

const CustomerSelector = ({
  customerSearchProps: {
    customers,
    isSearchingForCustomer,
    selectedCustomer,
    onSelectCustomer,
    onSearchForCustomer,
  },
  featureFlags,
  loadingFallback,
}: StorefrontPreviewerProps & {
  loadingFallback: ReactNode;
}) => {
  const [searchTerm, setSearchTerm] = useState("");

  const fullSelectedCustomer = customers.find((customer) => {
    return customer.id === selectedCustomer?.connectionId;
  });

  if (fullSelectedCustomer && selectedCustomer) {
    return (
      <div className="flex rounded-lg border border-zinc-300 p-4">
        <div className="flex flex-1 flex-col gap-1">
          <span className="font-semibold text-zinc-800">
            {fullSelectedCustomer.Retailer.Business.name}
          </span>
          <PostcodeRenderer
            postcodes={[
              fullSelectedCustomer.Retailer.Business.DeliveryAddress.postalCode,
            ]}
          />
          <div className="flex flex-wrap gap-2">
            <CustomerZoneBadge
              label="Price tier"
              icon="TAG_OUTLINE"
              value={
                fullSelectedCustomer.PriceTier?.name ?? "No price tier set"
              }
            />
            <CustomerZoneBadge
              label="Product catalogue"
              icon="BOOK_OUTLINE"
              value={
                fullSelectedCustomer.AvailabilityTier?.name ??
                "No product catalogue set"
              }
            />
            {featureFlags.isOrderRulesEnabled && (
              <CustomerZoneBadge
                label="Order rules"
                icon="CLIPBOARD_CHECK_OUTLINE"
                value={
                  fullSelectedCustomer.OrderRules?.name ?? "No order rules set"
                }
              />
            )}

            {fullSelectedCustomer.CustomerZone?.type === "direct" && (
              <CustomerZoneBadge
                value="Direct"
                className="bg-purple-800 text-white"
              />
            )}
            {fullSelectedCustomer.CustomerZone?.type === "thirdParty" && (
              <CustomerZoneBadge
                value="Non-Direct"
                className="bg-purple-100 text-purple-800"
              />
            )}
          </div>
        </div>
        <div className="flex items-center justify-center">
          <button
            type="button"
            className="cursor-pointer"
            onClick={() => {
              onSelectCustomer(null);
            }}
          >
            <Icon icon="CLOSE" />
          </button>
        </div>
      </div>
    );
  }

  const renderList = () => {
    return (
      <div
        className={twMerge(
          "rounded-lg border-gray-200",
          customers.length > 0 ? "border" : "border-none",
        )}
      >
        {customers.map((customer) => (
          <button
            type="button"
            key={customer.id}
            onClick={() => {
              onSelectCustomer({
                connectionId: customer.id,
                availabilityTierId: customer.AvailabilityTier?.id ?? null,
                priceTierId: customer.PriceTier?.id ?? null,
                orderRulesId: customer.OrderRules?.id ?? null,
              });
            }}
            className="flex w-full cursor-pointer flex-col px-4 py-2 text-start first-of-type:rounded-t-lg last-of-type:rounded-b-lg hover:bg-zinc-100"
          >
            <div className="flex items-center gap-2">
              <ProfilePicture
                size="medium"
                name={customer.Retailer.Business.name}
                logoUrl={customer.Retailer.Business.logoUrl}
              />
              <div className="font-semibold text-zinc-800">
                {customer.Retailer.Business.name}
              </div>
            </div>
          </button>
        ))}
      </div>
    );
  };

  const renderLoadingState = () => {
    return loadingFallback;
  };

  return (
    <div className="flex flex-col gap-2">
      <InputContainer className="w-full">
        <InputIcon icon="MAGNIFY" />
        <InputText
          placeholder="Enter a name"
          value={searchTerm}
          onChange={(event) => {
            const value = event.target.value;
            setSearchTerm(value);
            onSearchForCustomer(value);
          }}
        />
      </InputContainer>
      {isSearchingForCustomer && renderLoadingState()}
      {!isSearchingForCustomer && customers.length > 0 && renderList()}
    </div>
  );
};

const LoadingFallback = () => (
  <div className="flex flex-col gap-2 rounded-lg border border-gray-200 p-4">
    <SkeletonContainer className="w-full">
      <SkeletonText className="p-4" />
    </SkeletonContainer>
    <SkeletonContainer className="w-full">
      <SkeletonText className="p-4" />
    </SkeletonContainer>
  </div>
);

const CustomerTab = (props: StorefrontPreviewerProps) => {
  return (
    <div className="flex flex-col gap-2 p-4">
      <CustomerSelector {...props} loadingFallback={<LoadingFallback />} />
      {props.customerSearchProps.selectedCustomer && (
        <>
          <ProductList {...props} />
          {props.featureFlags.isOrderRulesEnabled && (
            <OrderRulesInformation {...props} />
          )}
        </>
      )}
    </div>
  );
};
